import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';

import {
  DownloadReportType,
  genericErrorMessage,
  TabName
} from '@epi-constants/downloadReports';
import { goToPage } from '@epi-actions/navigation';
import {
  REPORTS_QUERY_NAME,
  REPORTS_SEARCH_NAME
} from '@epi-constants/externalReport';
import { showNotification } from '@epi-actions/overlays';
import { FormFields } from '@epi-forms/helpers';
import { fetchGet } from '@epi-helpers/FetchHelpers';
import { companyBenefitGroupSettingsActiveBenefitsUrl } from '@epi-repositories/BenefitGroupSettingsRepository';
import { IReportType } from '@epi-models/pages/DownloadReports';
import { useGetReportTypesQuery } from '@epi-api/downloadReports/downloadReportsApi';

import { IncomeRegisterTab, LunchDeduction, ReportsTab } from '../components';
import { getOther, setActiveBenefits } from '../helpers';

export const useSetTabs = () => {
  const { t } = useTranslation();
  const { search, pathname } = useLocation();
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const [isLoading, setIsLoading] = useState(false);
  const {
    data,
    isLoading: isLoadingTypes,
    isSuccess
  } = useGetReportTypesQuery();

  const handleURLChange = tabName =>
    dispatch(goToPage(pathname + REPORTS_QUERY_NAME + tabName));

  const handleOnTabChange = tabName => {
    handleURLChange(tabName);
  };

  const urlParams = new URLSearchParams(search);
  const activeTab = TabName[urlParams.get(REPORTS_SEARCH_NAME) ?? 'Other'];

  const luchTab = {
    name: TabName.Lunch,
    label: t('download_reports.lunch_tab_name'),
    id: 'tab-lunch-deduction',
    body: <LunchDeduction />
  };

  const incomeTab = {
    name: TabName.Income,
    label: t('download_reports.income_tab_name'),
    id: 'tab-income-register-reports',
    body: <IncomeRegisterTab />
  };

  const otherTab = {
    name: TabName.Other,
    label: t('download_reports.other_tab_name'),
    id: 'tab-other-reports',
    body: <ReportsTab />
  };

  useEffect(() => {
    const onSuccess = res => {
      setValue(getOther(FormFields.ActiveBenefits), setActiveBenefits(res));
      setIsLoading(false);
    };

    const onFailure = () => {
      dispatch(showNotification({ message: genericErrorMessage }, 'error'));
      setIsLoading(false);
    };

    fetchGet(
      companyBenefitGroupSettingsActiveBenefitsUrl,
      {},
      onSuccess,
      onFailure
    );
  }, []);

  const tabsMap = {
    [DownloadReportType.OtherReport]: otherTab,
    [DownloadReportType.PayrollReport]: luchTab,
    [DownloadReportType.IncomeReport]: incomeTab
  };

  const tabs = data?.map((tabName: IReportType) => tabsMap[tabName]) ?? [];

  useEffect(() => {
    if (tabs.length) handleOnTabChange(tabs[0].name);
  }, [isSuccess]);

  return {
    tabs,
    handleOnTabChange,
    activeTab,
    isLoading: isLoading || isLoadingTypes
  };
};
